const HERO_IMAGE = '/assets/loginBackground.png';
const PRIMARY = '#0f62bd';
const PRIMARY_DARK = '#0a4a91';
const TEXT_MAIN = '#1a1f36';
const TEXT_SECONDARY = '#4a5568';
const BORDER = '#e2e8f0';
// Shared ghostly dark-navy used on alternating sections
const GHOST_BG = 'linear-gradient(160deg, rgba(12,28,72,0.92) 0%, rgba(16,54,148,0.88) 100%)';
const GHOST_CARD = 'rgba(255,255,255,0.07)';
const GHOST_CARD_BORDER = 'rgba(255,255,255,0.13)';

export default (theme) => ({

  // ─── Root ────────────────────────────────────────────────────────
  root: {
    minHeight: '100vh',
    backgroundColor: '#fff',
    fontFamily: 'Work Sans, Helvetica, Arial, sans-serif',
    color: TEXT_MAIN,
    overflowX: 'hidden',
  },

  // ─── Nav action buttons ───────────────────────────────────────────
  navSignIn: {
    textTransform: 'none',
    fontWeight: 600,
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.55)',
    '&:hover': {
      borderColor: '#fff',
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  navGetStarted: {
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: '#fff',
    color: PRIMARY,
    '&:hover': {
      backgroundColor: '#f0f4fb',
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },

  // ─── Hero ────────────────────────────────────────────────────────
  hero: {
    position: 'relative',
    height: '100vh',
    backgroundImage: `url(${HERO_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center right',
    display: 'flex',
    alignItems: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(100deg, rgba(10,26,60,0.88) 0%, rgba(15,98,189,0.72) 55%, rgba(15,98,189,0.2) 100%)',
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 680,
    padding: '64px 24px 80px 10%',
    [theme.breakpoints.down('sm')]: {
      padding: '48px 24px 64px',
      maxWidth: '100%',
    },
  },
  heroEyebrow: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 16,
  },
  heroHeadline: {
    color: '#fff',
    fontSize: 'clamp(2.2rem, 5vw, 3.4rem)',
    fontWeight: 700,
    lineHeight: 1.12,
    letterSpacing: '-0.02em',
    marginBottom: 20,
  },
  heroSubhead: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: '1.1rem',
    lineHeight: 1.65,
    maxWidth: 520,
    marginBottom: 36,
  },
  heroCtas: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *:not(:first-child)': {
      marginLeft: 14,
    },
  },
  heroPrimary: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    backgroundColor: '#fff',
    color: PRIMARY,
    padding: '12px 28px',
    '&:hover': {
      backgroundColor: '#f0f4fb',
    },
  },
  heroSecondary: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.7)',
    padding: '12px 28px',
    '&:hover': {
      borderColor: '#fff',
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  // animated scroll indicator
  scrollHint: {
    position: 'absolute',
    bottom: 36,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  scrollDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.7)',
    animation: '$bounce 1.8s ease-in-out infinite',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: 22,
      height: 36,
      border: '2px solid rgba(255,255,255,0.35)',
      borderRadius: 12,
      top: -15,
      left: '50%',
      transform: 'translateX(-50%)',
    },
  },
  '@keyframes bounce': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(10px)' },
  },

  // ─── Trust bar ───────────────────────────────────────────────────
  trustBar: {
    backgroundColor: '#f7f9fc',
    borderBottom: `1px solid ${BORDER}`,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '14px 24px',
  },
  trustItem: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: TEXT_SECONDARY,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    padding: '4px 20px',
  },
  trustDivider: {
    width: 1,
    height: 16,
    backgroundColor: BORDER,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },

  // ─── Shared section layout ────────────────────────────────────────
  sectionInner: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '0 24px',
  },
  sectionEyebrow: {
    color: PRIMARY,
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  sectionEyebrowLight: {
    color: PRIMARY,
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  sectionHeadline: {
    fontSize: 'clamp(1.6rem, 3.5vw, 2.25rem)',
    fontWeight: 700,
    lineHeight: 1.2,
    color: TEXT_MAIN,
    marginBottom: 24,
    maxWidth: 680,
  },
  sectionHeadlineLight: {
    fontSize: 'clamp(1.6rem, 3.5vw, 2.25rem)',
    fontWeight: 700,
    lineHeight: 1.2,
    color: TEXT_MAIN,
    marginBottom: 48,
    maxWidth: 680,
  },
  sectionEyebrowOnDark: {
    color: '#93c5fd',
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  sectionHeadlineOnDark: {
    fontSize: 'clamp(1.6rem, 3.5vw, 2.25rem)',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#fff',
    marginBottom: 16,
    maxWidth: 680,
  },

  // ─── Stats ───────────────────────────────────────────────────────
  stats: {
    padding: '56px 0',
    background: GHOST_BG,
  },
  statsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    '& > *': {
      flex: '1 1 180px',
      margin: '8px 0',
    },
  },
  statItem: {
    textAlign: 'center',
    padding: '12px 16px',
  },
  statValue: {
    fontSize: 'clamp(2rem, 5vw, 2.8rem)',
    fontWeight: 800,
    color: '#fff',
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
  },
  statLabel: {
    marginTop: 6,
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.65)',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
  },

  // ─── Intro / What is eSign ────────────────────────────────────────
  intro: {
    padding: '80px 0 72px',
    borderBottom: `1px solid ${BORDER}`,
  },
  introText: {
    maxWidth: 760,
  },
  introParagraph: {
    color: TEXT_SECONDARY,
    fontSize: '1rem',
    lineHeight: 1.75,
    marginBottom: 16,
  },

  // ─── Who it's for / Personas ─────────────────────────────────────
  personas: {
    padding: '96px 0',
    background: GHOST_BG,
  },
  personasGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: -14,
    marginRight: -14,
    marginTop: 40,
    '& > *': {
      flex: '1 1 280px',
      margin: 14,
    },
  },
  personaCard: {
    backgroundColor: GHOST_CARD,
    borderRadius: 16,
    padding: '32px 28px',
    border: `1px solid ${GHOST_CARD_BORDER}`,
    borderTop: '3px solid rgba(255,255,255,0.35)',
    backdropFilter: 'blur(8px)',
    transition: 'transform 0.22s ease, background 0.22s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      backgroundColor: 'rgba(255,255,255,0.12)',
    },
  },
  personaIconRing: {
    width: 52,
    height: 52,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  },
  personaIcon: {
    width: 24,
    height: 24,
    fill: '#93c5fd',
    color: '#93c5fd',
  },
  personaRole: {
    display: 'block',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#93c5fd',
    marginBottom: 8,
  },
  personaHeadline: {
    fontSize: '1.12rem',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 20,
    lineHeight: 1.3,
  },
  personaList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  personaItem: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.75)',
    lineHeight: 1.6,
    marginBottom: 10,
  },
  personaBullet: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: '#93c5fd',
    flexShrink: 0,
    marginTop: 6,
    marginRight: 10,
  },

  // ─── Steps / How it works ─────────────────────────────────────────
  steps: {
    padding: '96px 0 80px',
    backgroundColor: '#f7f9fc',
    borderBottom: `1px solid ${BORDER}`,
  },

  // Connector row: the horizontal dots + dashed line above the cards (desktop)
  stepsConnectorRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 48,
    marginBottom: -16,
    paddingLeft: 36,
    paddingRight: 36,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  stepsConnectorCell: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },
  stepsConnectorDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: PRIMARY,
    border: '3px solid #fff',
    boxShadow: `0 0 0 3px ${PRIMARY}55`,
    flexShrink: 0,
    zIndex: 1,
  },
  stepsConnectorLine: {
    flex: 1,
    height: 2,
    background: `linear-gradient(90deg, ${PRIMARY}, ${PRIMARY}66)`,
    marginLeft: 2,
  },

  stepsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 32,
    marginLeft: -10,
    marginRight: -10,
    '& > *': {
      flex: '1 1 220px',
      margin: 10,
    },
  },

  // Modern step card with a top accent bar
  stepCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: '32px 26px 28px',
    border: `1px solid ${BORDER}`,
    boxShadow: '0 2px 12px rgba(15,98,189,0.06)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 32px rgba(15,98,189,0.12)',
    },
    // thin primary accent at the top
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${PRIMARY}, #60a5fa)`,
    },
  },
  stepNumberBadge: {
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.08em',
    color: PRIMARY,
    backgroundColor: '#eef3fb',
    borderRadius: 6,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    marginBottom: 20,
  },
  stepIconRing: {
    width: 52,
    height: 52,
    borderRadius: 14,
    background: `linear-gradient(135deg, ${PRIMARY}22, ${PRIMARY}0a)`,
    border: `1px solid ${PRIMARY}22`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  stepIcon: {
    width: 24,
    height: 24,
    fill: PRIMARY,
    color: PRIMARY,
  },
  stepTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: TEXT_MAIN,
    marginBottom: 10,
  },
  stepText: {
    fontSize: '0.9rem',
    color: TEXT_SECONDARY,
    lineHeight: 1.7,
  },

  // ─── Product demo ────────────────────────────────────────────────
  demo: {
    padding: '96px 0',
    background: GHOST_BG,
  },
  demoSubhead: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: '1rem',
    lineHeight: 1.75,
    maxWidth: 600,
    marginBottom: 52,
  },
  demoGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: -16,
    marginRight: -16,
    '& > *': {
      flex: '1 1 340px',
      margin: 16,
    },
  },
  demoPanel: {
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: '#1a2744',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
  },
  demoBrowserBar: {
    backgroundColor: '#111827',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
  },
  demoDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 6,
    flexShrink: 0,
  },
  demoUrl: {
    marginLeft: 12,
    fontSize: '0.72rem',
    color: 'rgba(255,255,255,0.35)',
    fontFamily: 'monospace',
  },
  demoScreen: {
    overflow: 'hidden',
    lineHeight: 0,
    maxHeight: 340,
  },
  demoGif: {
    width: '100%',
    display: 'block',
    objectFit: 'cover',
  },
  demoPanelCaption: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '20px 20px 22px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  demoCaptionIcon: {
    width: 20,
    height: 20,
    fill: '#60a5fa',
    color: '#60a5fa',
    flexShrink: 0,
    marginTop: 2,
    marginRight: 14,
  },
  demoCaptionTitle: {
    display: 'block',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.95rem',
    marginBottom: 4,
  },
  demoCaptionText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '0.85rem',
    lineHeight: 1.65,
    margin: 0,
  },

  // ─── Field types ─────────────────────────────────────────────────
  fieldTypes: {
    padding: '96px 0',
    background: GHOST_BG,
  },
  fieldTypesSubhead: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '1rem',
    lineHeight: 1.75,
    maxWidth: 640,
    marginBottom: 48,
  },
  fieldTypesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    marginLeft: -12,
    marginRight: -12,
    '& > *': {
      flex: '1 1 220px',
      margin: 12,
    },
  },
  fieldTypeCard: {
    backgroundColor: GHOST_CARD,
    borderRadius: 14,
    padding: '28px 24px',
    border: `1px solid ${GHOST_CARD_BORDER}`,
    backdropFilter: 'blur(8px)',
    position: 'relative',
    transition: 'transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      backgroundColor: 'rgba(255,255,255,0.12)',
      boxShadow: '0 14px 32px rgba(0,0,0,0.22)',
    },
  },
  fieldTypeIconRing: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  fieldTypeIcon: {
    width: 22,
    height: 22,
    fill: '#93c5fd',
    color: '#93c5fd',
  },
  fieldTypeLabel: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 6,
  },
  fieldTypeDesc: {
    fontSize: '0.87rem',
    color: 'rgba(255,255,255,0.68)',
    lineHeight: 1.65,
    margin: 0,
  },

  // ─── Benefits ────────────────────────────────────────────────────
  benefits: {
    padding: '96px 0 104px',
    backgroundColor: '#fff',
    borderTop: `1px solid ${BORDER}`,
    borderBottom: `1px solid ${BORDER}`,
  },
  benefitsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    marginLeft: -14,
    marginRight: -14,
    marginTop: 12,
    '& > *': {
      flex: '1 1 280px',
      margin: 14,
    },
  },
  benefitCard: {
    padding: '32px 28px 28px',
    borderRadius: 16,
    backgroundColor: '#fff',
    border: `1px solid ${BORDER}`,
    boxShadow: '0 2px 16px rgba(15,98,189,0.05)',
    transition: 'transform 0.22s ease, box-shadow 0.22s ease',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    boxSizing: 'border-box',
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 16px 40px rgba(15,98,189,0.12)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -24,
      right: -24,
      width: 120,
      height: 120,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)',
      pointerEvents: 'none',
    },
  },
  benefitTag: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#3b82f6',
    backgroundColor: 'rgba(59,130,246,0.10)',
    borderRadius: 6,
    padding: '3px 10px',
    marginBottom: 18,
  },
  benefitIconRing: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: 'rgba(59,130,246,0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  benefitIcon: {
    width: 26,
    height: 26,
    fill: '#3b82f6',
    color: '#3b82f6',
  },
  benefitTitle: {
    fontSize: '1.08rem',
    fontWeight: 700,
    color: TEXT_MAIN,
    marginBottom: 10,
  },
  benefitText: {
    fontSize: '0.9rem',
    color: TEXT_SECONDARY,
    lineHeight: 1.72,
  },

  // ─── Security ────────────────────────────────────────────────────
  security: {
    padding: '96px 0',
    background: GHOST_BG,
  },
  securitySubhead: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '1rem',
    lineHeight: 1.75,
    maxWidth: 680,
    marginBottom: 48,
  },
  securityGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    marginLeft: -16,
    marginRight: -16,
    '& > *': {
      flex: '1 1 260px',
      margin: 16,
    },
  },
  securityCard: {
    backgroundColor: GHOST_CARD,
    borderRadius: 14,
    padding: '32px 28px',
    border: `1px solid ${GHOST_CARD_BORDER}`,
    backdropFilter: 'blur(8px)',
    position: 'relative',
    height: '100%',
    boxSizing: 'border-box',
    transition: 'transform 0.22s ease, background 0.22s ease, box-shadow 0.22s ease',
    '&:hover': {
      transform: 'translateY(-6px)',
      backgroundColor: 'rgba(255,255,255,0.12)',
      boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
    },
  },
  securityDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginBottom: 16,
  },
  securityTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 8,
  },
  securityText: {
    fontSize: '0.88rem',
    color: 'rgba(255,255,255,0.68)',
    lineHeight: 1.7,
  },

  // ─── FAQ ─────────────────────────────────────────────────────────
  faq: {
    padding: '96px 0',
    backgroundColor: '#fff',
  },
  faqInner: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '0 24px',
  },
  faqList: {
    marginTop: 40,
    borderTop: `1px solid ${BORDER}`,
  },
  faqItem: {
    borderBottom: `1px solid ${BORDER}`,
  },
  faqItemOpen: {
    backgroundColor: '#f9fafb',
  },
  faqQuestion: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 4px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_MAIN,
    fontFamily: 'Work Sans, Helvetica, Arial, sans-serif',
    '&:hover': {
      color: PRIMARY,
    },
  },
  faqChevron: {
    fontSize: '1.3rem',
    color: TEXT_SECONDARY,
    transition: 'transform 0.25s ease',
    flexShrink: 0,
    marginLeft: 16,
    display: 'inline-block',
  },
  faqChevronOpen: {
    transform: 'rotate(180deg)',
  },
  faqAnswer: {
    padding: '0 4px 20px',
    '& p': {
      fontSize: '0.95rem',
      color: TEXT_SECONDARY,
      lineHeight: 1.75,
      margin: 0,
    },
  },

  // ─── Verify CTA ──────────────────────────────────────────────────
  verify: {
    padding: '64px 0',
    borderBottom: `1px solid ${BORDER}`,
    borderTop: `1px solid ${BORDER}`,
  },
  verifyInner: {
    maxWidth: 700,
    margin: '0 auto',
    padding: '0 24px',
    textAlign: 'center',
  },
  verifyHeadline: {
    fontSize: 'clamp(1.5rem, 3vw, 2rem)',
    fontWeight: 700,
    color: TEXT_MAIN,
    marginBottom: 12,
  },
  verifyText: {
    color: TEXT_SECONDARY,
    fontSize: '1rem',
    lineHeight: 1.7,
    marginBottom: 28,
  },
  verifyBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    color: PRIMARY,
    borderColor: PRIMARY,
    padding: '11px 28px',
    '&:hover': {
      backgroundColor: '#eef3fb',
    },
  },

  // ─── Final CTA ───────────────────────────────────────────────────
  finalCta: {
    padding: '96px 0',
    textAlign: 'center',
    backgroundImage: `url(${HERO_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(135deg, rgba(10,26,60,0.92) 0%, rgba(15,98,189,0.80) 100%)',
    },
  },
  finalHeadline: {
    position: 'relative',
    zIndex: 1,
    fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 12,
  },
  finalSub: {
    position: 'relative',
    zIndex: 1,
    color: 'rgba(255,255,255,0.82)',
    fontSize: '1rem',
    marginBottom: 32,
  },
  finalBtn: {
    position: 'relative',
    zIndex: 1,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '1rem',
    backgroundColor: '#fff',
    color: PRIMARY,
    padding: '13px 36px',
    '&:hover': {
      backgroundColor: '#f0f4fb',
    },
  },

  // ─── Footer ──────────────────────────────────────────────────────
  footer: {
    backgroundColor: '#111827',
    padding: '28px 24px',
  },
  footerInner: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerLogo: {
    height: 28,
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      height: '100%',
      width: 'auto',
      filter: 'brightness(0) invert(1)',
      opacity: 0.8,
    },
  },
  footerCopy: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: '0.82rem',
  },
  footerLinks: {
    display: 'flex',
    '& > *:not(:first-child)': {
      marginLeft: 20,
    },
  },
  footerLink: {
    color: 'rgba(255,255,255,0.55)',
    textDecoration: 'none',
    fontSize: '0.82rem',
    '&:hover': {
      color: 'rgba(255,255,255,0.9)',
    },
  },
});
